// src/services/recipesService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class RecipesService {
  
  // 🍴 Get all recipes with pagination support
  async getAllRecipes(params = {}) {
    try {
      console.log('🍴 Fetching all recipes with params:', params)
      
      const response = await ApiService.get(AppURLs.RECIPES.GET_ALL, params)
      console.log('✅ Recipes fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching recipes:', error)
      throw error
    }
  }

  // 🍴 Get recipe by ID
  async getRecipeById(id) {
    try {
      console.log('🍴 Fetching recipe by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.RECIPES.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Recipe fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching recipe:', error)
      throw error
    }
  }

  // 🍴 Create new recipe
  async createRecipe(recipeData) {
    try {
      console.log('🍴 Creating new recipe with data:', recipeData)
      
      // Validate required fields
      this.validateRecipeData(recipeData)
      
      const response = await ApiService.post(AppURLs.RECIPES.CREATE, recipeData)
      console.log('✅ Recipe created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating recipe:', error)
      throw error
    }
  }

  // 🍴 Update recipe using PATCH
  async updateRecipe(id, recipeData) {
    try {
      console.log('🍴 Updating recipe:', { id, recipeData })
      
      // Validate partial data for updates
      this.validatePartialRecipeData(recipeData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.RECIPES.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, recipeData)
      console.log('✅ Recipe updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating recipe:', error)
      throw error
    }
  }

  // 🍴 Delete recipe
  async deleteRecipe(id) {
    try {
      console.log('🍴 Deleting recipe with ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.RECIPES.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Recipe deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting recipe:', error)
      throw error
    }
  }

  // 🍴 Get recipes by product ID
  async getRecipesByProduct(productId) {
    try {
      console.log('🍴 Fetching recipes by product ID:', productId)
      const endpoint = ApiService.replaceUrlParams(AppURLs.RECIPES.BY_PRODUCT, { productId })
      const response = await ApiService.get(endpoint)
      console.log('✅ Recipes by product fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching recipes by product:', error)
      throw error
    }
  }

  // 🍴 Search recipes
  async searchRecipes(query, params = {}) {
    try {
      console.log('🍴 Searching recipes with query:', query)
      const searchParams = { ...params, q: query }
      const response = await ApiService.get(AppURLs.RECIPES.SEARCH, searchParams)
      console.log('✅ Recipe search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching recipes:', error)
      throw error
    }
  }

  // 🍴 Validate name
  async validateName(name, excludeId = null) {
    try {
      console.log('🍴 Validating recipe name:', name)
      const params = { name }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.RECIPES.VALIDATE_NAME, params)
      console.log('✅ Name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating recipe name:', error)
      throw error
    }
  }

  // 🍴 Bulk delete recipes
  async bulkDelete(ids) {
    try {
      console.log('🍴 Bulk deleting recipes with IDs:', ids)
      const response = await ApiService.post(AppURLs.RECIPES.BULK_DELETE, { ids })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk delete:', error)
      throw error
    }
  }

  // 🍴 Bulk status update
  async bulkUpdateStatus(ids, isActive) {
    try {
      console.log('🍴 Bulk updating recipe status:', { ids, isActive })
      const response = await ApiService.post(AppURLs.RECIPES.BULK_STATUS, { ids, isActive })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk status update:', error)
      throw error
    }
  }

  // 🍴 Get recipes statistics
  async getStats() {
    try {
      console.log('🍴 Fetching recipe statistics')
      const response = await ApiService.get(AppURLs.RECIPES.STATS)
      console.log('✅ Recipe stats fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching recipe stats:', error)
      throw error
    }
  }

  // 🔒 Validation Methods

  // Validate recipe data for creation
  validateRecipeData(data) {
    const errors = []

    // Required fields validation
    if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
      errors.push('Recipe name is required and must be a non-empty string')
    }

    if (!data.product || typeof data.product !== 'string') {
      errors.push('Product ID is required and must be a valid string')
    }

    if (data.cost === undefined || data.cost === null || isNaN(data.cost) || data.cost < 0) {
      errors.push('Cost is required and must be a non-negative number')
    }

    if (!Array.isArray(data.rawMaterialList) || data.rawMaterialList.length === 0) {
      errors.push('Raw material list is required and must contain at least one item')
    }

    // Validate raw material list items
    if (Array.isArray(data.rawMaterialList)) {
      data.rawMaterialList.forEach((item, index) => {
        if (!item.rawMaterial || typeof item.rawMaterial !== 'string') {
          errors.push(`Raw material ID is required for item at index ${index}`)
        }
        if (item.quantity === undefined || item.quantity === null || isNaN(item.quantity) || item.quantity <= 0) {
          errors.push(`Valid quantity is required for item at index ${index}`)
        }
      })
    }

    // String length validations
    if (data.name && data.name.length > 100) {
      errors.push('Recipe name must not exceed 100 characters')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // Validate partial recipe data for updates
  validatePartialRecipeData(data) {
    const errors = []

    // Optional fields validation (only validate if provided)
    if (data.name !== undefined) {
      if (typeof data.name !== 'string' || data.name.trim().length === 0) {
        errors.push('Recipe name must be a non-empty string')
      }
      if (data.name.length > 100) {
        errors.push('Recipe name must not exceed 100 characters')
      }
    }

    if (data.product !== undefined && (typeof data.product !== 'string' || data.product.trim().length === 0)) {
      errors.push('Product ID must be a valid non-empty string')
    }

    if (data.cost !== undefined && (isNaN(data.cost) || data.cost < 0)) {
      errors.push('Cost must be a non-negative number')
    }

    if (data.rawMaterialList !== undefined) {
      if (!Array.isArray(data.rawMaterialList)) {
        errors.push('Raw material list must be an array')
      } else if (data.rawMaterialList.length === 0) {
        errors.push('Raw material list must contain at least one item')
      } else {
        // Validate raw material list items
        data.rawMaterialList.forEach((item, index) => {
          if (!item.rawMaterial || typeof item.rawMaterial !== 'string') {
            errors.push(`Raw material ID is required for item at index ${index}`)
          }
          if (item.quantity === undefined || item.quantity === null || isNaN(item.quantity) || item.quantity <= 0) {
            errors.push(`Valid quantity is required for item at index ${index}`)
          }
        })
      }
    }

    if (data.isActive !== undefined && typeof data.isActive !== 'boolean') {
      errors.push('isActive must be a boolean value')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // 🎯 Utility Methods

  // Format recipe data for display
  formatRecipeForDisplay(recipe) {
    if (!recipe) return null

    return {
      id: recipe._id,
      name: recipe.name,
      cost: recipe.cost,
      product: recipe.product?.name || 'Unknown Product',
      productId: recipe.product?._id || recipe.product,
      rawMaterialList: recipe.rawMaterialList?.map(item => ({
        id: item._id,
        rawMaterial: item.rawMaterial?.name || 'Unknown Material',
        rawMaterialId: item.rawMaterial?._id || item.rawMaterial,
        quantity: item.quantity,
        unit: item.rawMaterial?.unit?.abbreviation || 'unit'
      })) || [],
      isActive: recipe.isActive !== false,
      createdAt: recipe.createdAt,
      updatedAt: recipe.updatedAt,
      createdBy: recipe.createdBy?.username || 'Unknown'
    }
  }

  // Calculate total cost from raw materials
  calculateTotalCost(rawMaterialList) {
    if (!Array.isArray(rawMaterialList)) return 0
    
    return rawMaterialList.reduce((total, item) => {
      const quantity = parseFloat(item.quantity) || 0
      const cost = parseFloat(item.rawMaterial?.cost || 0)
      return total + (quantity * cost)
    }, 0)
  }

  // Validate raw material quantities
  validateRawMaterialQuantities(rawMaterialList, availableInventory = []) {
    if (!Array.isArray(rawMaterialList)) return { valid: false, errors: ['Invalid raw material list'] }
    
    const errors = []
    
    rawMaterialList.forEach((item, index) => {
      const quantity = parseFloat(item.quantity)
      const materialId = item.rawMaterial?._id || item.rawMaterial
      
      if (isNaN(quantity) || quantity <= 0) {
        errors.push(`Invalid quantity for item ${index + 1}`)
      }
      
      // Check against inventory if provided
      if (availableInventory.length > 0) {
        const inventory = availableInventory.find(inv => 
          inv.rawMaterial?._id === materialId || inv.rawMaterial === materialId
        )
        
        if (inventory && quantity > inventory.currentStock) {
          errors.push(`Insufficient stock for ${item.rawMaterial?.name || 'material'}: required ${quantity}, available ${inventory.currentStock}`)
        }
      }
    })
    
    return {
      valid: errors.length === 0,
      errors
    }
  }
}

export default new RecipesService()